<script setup lang="ts">
import { ref } from 'vue';
import { AkDrawer } from '@ankorstore/design-system';
import { AkDrawerProps } from './types';

const isOpen = defineModel({
  type: Boolean,
  default: false,
});
const setIsOpen = (value: boolean) => {
  if (value === isOpen.value) {
    return;
  }
  isOpen.value = value;
};

withDefaults(defineProps<AkDrawerProps>(), {
  withCloser: true,
  closeOnOutsideClick: true,
  // Mirrors AkDrawer own default, and is load-bearing rather than decorative: Vue casts
  // an absent Boolean prop with no default to `false`, so without this line
  // `v-bind="$props"` below would forward an explicit `overlay: false` and no consumer of
  // useDrawer() would ever paint a scrim.
  overlay: true,
});

const openDrawer = () => {
  setIsOpen(true);
};
const closeDrawer = () => {
  setIsOpen(false);
};

defineExpose({ open: openDrawer, close: closeDrawer });

const drawer = ref<InstanceType<typeof AkDrawer> | null>(null);
</script>

<template>
  <Teleport to="body">
    <div class="drawer-wrapper">
      <AkDrawer
        v-bind="$props"
        ref="drawer"
        :is-open="isOpen"
        @update:open="openDrawer()"
        @update:close="closeDrawer()"
      >
        <slot :close-drawer="() => closeDrawer()" />
      </AkDrawer>
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
// A stacking context and nothing else. Since DS 29.1.0 AkDrawer paints its own scrim
// inside a ds-fixed ds-inset-0 .ak-drawer__layer, so the only job left here is to lift the
// whole drawer group over the sticky site header (.ak-header, z-index 200).

// Deliberately no inset: every child is out of flow, so this box is 0x0 and can intercept
// nothing. And deliberately no transform / filter / contain / will-change -- any one of
// those would make this element a containing block for the fixed layer inside it and pin a
// viewport-sized scrim to that 0x0 box.
.drawer-wrapper {
  z-index: 1050;
  position: fixed;
}
</style>
